import axios from 'axios';
import { videoInterviewDataStoreKey } from './videoInterview.const';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { notificationAlert } from '../notifications';
import { formatErrorMessage } from '../../utils';
import {
  createUserVideoInterviewPath,
  videoInterviewReminderDatePath,
  restartUserVideoInterviewPath,
  viewUserVideoInterviewPath,
  getAvailableVideoInterviewPath,
  skipUserVideoInterviewPath,
  getUserVideoInterviewUploadUrlPath,
  finishUserVideoInterviewQuestionPath,
  finishUserVideoInterviewPath,
  requestViewUserVideoInterviewPath,
  getViewUserVideoInterviewVideoUrlPath,
} from '../../constants/apiEndpoints';
import { fetchUser, updateUserVideoInterview } from '../user';
import { ISetVideoInterviewReminderPayload, IUserData } from '../../types';
import {
  IUserVideoInterviewUploadQuestionBody,
  IUserVideoInterviewUploadQuestionResponse,
  IVideoInterviewFinishQuestionBody,
  MatchingVIPosition,
} from 'src/types/videoInterviewData';
import {
  IUserVideoInterviewData,
  IViewUserVideoInterviewData,
} from 'src/types/userVideoInterviewData';

export const getAvailableVideoInterview = createAsyncThunk(
  `${videoInterviewDataStoreKey}/getAvailableVideoInterview`,
  async () => {
    const response = await axios.get<MatchingVIPosition[]>(
      getAvailableVideoInterviewPath(),
    );
    return response.data;
  },
);

export const startVideoInterview = createAsyncThunk(
  `${videoInterviewDataStoreKey}/startVideoInterview`,
  async ({ videoInterviewId }: { videoInterviewId: string }, thunkAPI) => {
    try {
      const response = await axios.post<IUserVideoInterviewData>(
        createUserVideoInterviewPath(),
        {
          videoInterviewId,
          agent: window.navigator.userAgent,
        },
      );

      await thunkAPI.dispatch(fetchUser());
      await thunkAPI.dispatch(getAvailableVideoInterview());

      return response.data;
    } catch (err: any) {
      thunkAPI.dispatch(
        notificationAlert(formatErrorMessage(err), { variant: 'error' }),
      );
      throw new Error(err);
    }
  },
);

export const setVideoInterviewReminderDate = createAsyncThunk(
  `${videoInterviewDataStoreKey}/setVideoInterviewReminderDate`,
  async (
    { videoInterviewReminderDate }: ISetVideoInterviewReminderPayload,
    thunkAPI,
  ) => {
    try {
      const response = await axios.post<IUserData>(
        videoInterviewReminderDatePath(),
        {
          videoInterviewReminderDate,
        },
      );
      thunkAPI.dispatch(fetchUser());
      return response.data;
    } catch (err: any) {
      thunkAPI.dispatch(
        notificationAlert(formatErrorMessage(err), { variant: 'error' }),
      );
      throw new Error(err);
    }
  },
);

export const skipVideoInterview = createAsyncThunk(
  `${videoInterviewDataStoreKey}/skipVideoInterview`,
  async (_, thunkAPI) => {
    try {
      await axios.post(skipUserVideoInterviewPath());

      await thunkAPI.dispatch(fetchUser());
      await thunkAPI.dispatch(getAvailableVideoInterview());
    } catch (err: any) {
      thunkAPI.dispatch(
        notificationAlert(formatErrorMessage(err), { variant: 'error' }),
      );
      throw new Error(err);
    }
  },
);

export const restartVideoInterview = createAsyncThunk(
  `${videoInterviewDataStoreKey}/restartVideoInterview`,
  async (userVideoInterviewId: string, thunkAPI) => {
    try {
      const response = await axios.post<IUserVideoInterviewData>(
        restartUserVideoInterviewPath(userVideoInterviewId),
        { agent: window.navigator.userAgent },
      );

      await thunkAPI.dispatch(fetchUser());

      return response.data;
    } catch (err: any) {
      thunkAPI.dispatch(
        notificationAlert(formatErrorMessage(err), { variant: 'error' }),
      );
      throw new Error(err);
    }
  },
);

export const getVideoInterviewUploadUrl = createAsyncThunk(
  `${videoInterviewDataStoreKey}/getVideoInterviewUploadUrl`,
  async (
    {
      userVideoInterviewId,
      questionNumber,
      record,
    }: IUserVideoInterviewUploadQuestionBody,
    thunkAPI,
  ) => {
    try {
      const response =
        await axios.post<IUserVideoInterviewUploadQuestionResponse>(
          getUserVideoInterviewUploadUrlPath(
            userVideoInterviewId,
            questionNumber,
          ),
          {
            contentType: record.type,
            contentLength: record.size,
          },
        );

      return response.data;
    } catch (err: any) {
      thunkAPI.dispatch(
        notificationAlert(formatErrorMessage(err), { variant: 'error' }),
      );
      throw new Error(err);
    }
  },
);

export const finishVideoInterviewQuestion = createAsyncThunk(
  `${videoInterviewDataStoreKey}/finishVideoInterviewQuestion`,
  async (
    {
      userVideoInterviewId,
      questionNumber,
      fileName,
    }: IVideoInterviewFinishQuestionBody,
    thunkAPI,
  ) => {
    try {
      const response = await axios.post<IUserVideoInterviewData>(
        finishUserVideoInterviewQuestionPath(
          userVideoInterviewId,
          questionNumber,
        ),
        {
          fileName,
        },
      );

      thunkAPI.dispatch(updateUserVideoInterview(response.data));

      return response.data;
    } catch (err: any) {
      thunkAPI.dispatch(
        notificationAlert(formatErrorMessage(err), { variant: 'error' }),
      );
      throw new Error(err);
    }
  },
);

export const finishVideoInterview = createAsyncThunk(
  `${videoInterviewDataStoreKey}/finishVideoInterview`,
  async (
    { userVideoInterviewId }: { userVideoInterviewId: string },
    thunkAPI,
  ) => {
    try {
      const response = await axios.post<IUserVideoInterviewData>(
        finishUserVideoInterviewPath(userVideoInterviewId),
      );

      await thunkAPI.dispatch(fetchUser());
      await thunkAPI.dispatch(getAvailableVideoInterview());

      return response.data;
    } catch (err: any) {
      thunkAPI.dispatch(
        notificationAlert(formatErrorMessage(err), { variant: 'error' }),
      );
      throw new Error(err);
    }
  },
);

export const viewUserVideoInterview = createAsyncThunk(
  `${videoInterviewDataStoreKey}/viewUserVideoInterview`,
  async ({ token }: { token: string }) => {
    try {
      const response = await axios.get(viewUserVideoInterviewPath(), {
        params: { token },
      });

      return response.data as IViewUserVideoInterviewData;
    } catch (err: any) {
      throw new Error(err);
    }
  },
);

export const getViewUserVideoInterviewViewUrl = createAsyncThunk(
  `${videoInterviewDataStoreKey}/getViewUserVideoInterviewViewUrl`,
  async ({ fileName, token }: { fileName: string; token: string }) => {
    try {
      const response = await axios.get<string>(
        getViewUserVideoInterviewVideoUrlPath(),
        {
          params: { fileName, token },
        },
      );

      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  },
);

export const requestViewUserVideoInterviewLink = createAsyncThunk(
  `${videoInterviewDataStoreKey}/requestViewUserVideoInterviewLink`,
  async (
    {
      userVideoInterviewId,
      email,
    }: {
      userVideoInterviewId: string;
      email: string;
    },
    { dispatch },
  ) => {
    try {
      const response = await axios.post(
        requestViewUserVideoInterviewPath(userVideoInterviewId),
        {
          email,
        },
      );

      dispatch(
        notificationAlert('Request has been sent', {
          variant: 'success',
        }),
      );

      return response.data;
    } catch (err: any) {
      dispatch(
        notificationAlert(err.response.data || 'Something went wrong', {
          variant: 'error',
        }),
      );
      throw new Error(err);
    }
  },
);
